/**
 * CapabilityMatrix — docs/08 §8.4: checkbox theo nhóm + bộ đếm "đang bật / tổng".
 * Dùng cho cả `roles.html` (4 nhóm hệ thống) và `domain-roles.html` (3 nhóm tên miền).
 *
 * OQ-246 — hai sai lệch so với prototype, cùng lộ ra trong một ảnh chụp:
 *
 * 1. Mô tả dính vào nhãn. Prototype dùng `<div class="sub">`; ở đây là `<span>`,
 *    và CSS chép nguyên nên không còn gì bắt xuống dòng. Nay `.cap .sub` khai
 *    `display: block` — chữa cho mọi thẻ, không riêng chỗ này.
 *
 * 2. Mã capability (`dom.view`, `share.grant`…) in thẳng vào luồng chữ tiếng
 *    Việt. Prototype không có nó. Người quản trị đọc "…hoặc được cấpdom.view"
 *    và chỉ thấy một mớ chữ lẫn lộn.
 *
 * OQ-247 — mã capability rời khỏi giao diện **hoàn toàn**, kể cả tooltip.
 *
 * Bản vá OQ-246 giữ nó trong `title` với ý "không vứt thông tin đi". Chủ dự án
 * bác: *"các mã capability là ký hiệu riêng của code thì không nên hiển thị lên
 * frontend (kể cả trong tooltip)"* — và đúng vậy. `dns.view` không giúp người
 * quản trị quyết định gì; nó chỉ có nghĩa với người đọc mã nguồn.
 *
 * `cap.id` vẫn chạy trong bộ nhớ vì `onToggle` cần nó. Chỉ là không lên màn hình.
 */

import { useT } from '../lib/i18n';
import type { CapabilityGroup } from '../lib/types';

export function CapabilityMatrix({
  groups,
  selected,
  onToggle,
  readOnly = false,
  lockedNote,
}: {
  groups: CapabilityGroup[];
  selected: readonly string[];
  onToggle: (id: string, next: boolean) => void;
  readOnly?: boolean;
  lockedNote?: string;
}) {
  const t = useT();
  const chosen = new Set(selected);

  return (
    <div data-od-id="capability-matrix">
      {groups.map((group) => {
        const on = group.capabilities.filter((cap) => chosen.has(cap.id)).length;
        return (
          <section className="cap-group" key={group.group}>
            <div className="row" style={{ gap: 10 }}>
              <span className="eyebrow grow">{group.group}</span>
              <span className="small muted mono">
                {t('roles.capabilityCount', { on, total: group.capabilities.length })}
              </span>
            </div>

            <div className="cap-list">
              {group.capabilities.map((cap) => {
                const checked = chosen.has(cap.id);
                return (
                  <label
                    className={readOnly ? 'cap locked' : 'cap'}
                    key={cap.id}
                    title={readOnly ? lockedNote : undefined}
                  >
                    <input
                      className="check"
                      type="checkbox"
                      checked={checked}
                      disabled={readOnly}
                      onChange={(event) => onToggle(cap.id, event.target.checked)}
                    />
                    <span>
                      <b>{cap.name}</b>
                      <span className="sub">{cap.description}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** Bộ đếm tổng "đang bật / tổng" cho phần đầu thẻ. */
export function CapabilityCount({
  groups,
  selected,
}: {
  groups: CapabilityGroup[];
  selected: readonly string[];
}) {
  const total = groups.reduce((sum, g) => sum + g.capabilities.length, 0);
  const chosen = new Set(selected);
  const on = groups.reduce(
    (sum, g) => sum + g.capabilities.filter((c) => chosen.has(c.id)).length,
    0,
  );
  return (
    <span className="chip mono">
      {on} / {total}
    </span>
  );
}
